/**
 * ContextualSuggestionChips — static chip grid in the sidecar header.
 *
 * Phase 0: 5 chip definitions, filtered by `showWhen(brief)`.  Phase 2 swaps
 * this for a dynamic chip generator driven by health + annotation state.
 */

import { GitBranch, Lightbulb, MessageSquare, Sparkles, Target } from 'lucide-react';
import type { GenerationBrief } from '@/services/api';

interface ChipDefinition {
  label: string;
  prompt: string;
  Icon: typeof Lightbulb;
  showWhen: (brief: GenerationBrief) => boolean;
}

const STATIC_CHIPS: ChipDefinition[] = [
  {
    label: 'Improve the Insight',
    prompt:
      'Review the Insight section of this brief and suggest a sharper tension statement.',
    Icon: Lightbulb,
    showWhen: (b) => b.status === 'draft',
  },
  {
    label: 'Check brand voice',
    prompt:
      'Does the Brand Voice section align with the vocabulary in the Message section? Flag any inconsistencies.',
    Icon: MessageSquare,
    showWhen: (b) => !!b.brand_voice && !!b.message,
  },
  {
    label: 'Suggest more angles',
    prompt:
      "Based on the brief's persona and insight, suggest 3 more creative angles we haven't tried.",
    Icon: Sparkles,
    showWhen: () => true,
  },
  {
    label: 'Explain PDO grounding',
    prompt:
      "Walk me through which buyer scenarios and PDO concepts are driving the creative direction in this brief.",
    Icon: GitBranch,
    showWhen: () => true,
  },
  {
    label: 'Review creative strategy',
    prompt:
      "Run a persona-grounded review of this brief's creative strategy. Does the insight, voice, and message form a coherent whole?",
    Icon: Target,
    showWhen: (b) => b.status === 'active',
  },
];

interface ContextualSuggestionChipsProps {
  brief: GenerationBrief | null;
  onChipClick: (prompt: string) => void;
  disabled?: boolean;
  /** Maximum chips to show (default 4). */
  max?: number;
}

export function ContextualSuggestionChips({
  brief,
  onChipClick,
  disabled,
  max = 4,
}: ContextualSuggestionChipsProps) {
  if (!brief) return null;
  const chips = STATIC_CHIPS.filter((c) => c.showWhen(brief)).slice(0, max);
  if (chips.length === 0) return null;

  return (
    <div className="px-3 py-2 border-b border-slate-100 bg-gradient-to-r from-violet-50/40 to-white">
      <div className="text-[9px] uppercase tracking-wider text-violet-400 mb-1.5 font-semibold">
        Suggested actions
      </div>
      <div className="flex flex-wrap gap-1.5">
        {chips.map(({ label, prompt, Icon }) => (
          <button
            key={label}
            disabled={disabled}
            onClick={() => onChipClick(prompt)}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border bg-white border-slate-200 text-slate-600 hover:border-brand-purple hover:text-brand-purple transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon size={10} />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
